import {
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState
} from "react";
import { notifications } from "@mantine/notifications";
import { ChatStatus } from "ai";
import { v4 as uuidv4 } from "uuid";

import { Dropzone } from "@mantine/dropzone";
import { ActionIcon, Group, Tooltip } from "@mantine/core";
import { TbArrowUp, TbPlus, TbSquare } from "react-icons/tb";

import ChatInputFile from "./ChatInputFile";
import ChatTextInput from "./ChatTextInput";

import { acceptedUploadFileExtensions } from "./constants";
import { uploadFile } from "modules/core/api/upload";
import { getChatPresignedUrls } from "modules/chat/api/attachments";

import classes from "./styles.module.css";

import { IFileAttachment } from "modules/core/types/entities";

export type FileUpload = {
  id: string;
  file: File;
  url: string;
  presignedURL: string;
  publicURL: string;
  progress: number;
  status: "uploading" | "completed" | "error";
};

type ChatInputProps = {
  chatStatus: ChatStatus;
  message: string;
  setMessage: (message: string) => void;
  onSend: (message: string, files?: FileUpload[]) => void;
  onStop: () => void;
};

const ChatInput = forwardRef<{ focus: () => void }, ChatInputProps>(
  (props, ref) => {
    const { chatStatus, message, setMessage, onSend, onStop } = props;

    const [fileUploads, setFileUploads] = useState<FileUpload[]>([]);

    const openRef = useRef<() => void>(null);

    const handleDrop = useCallback(async (files: File[]) => {
      const newFileUploads = files.map(
        file =>
          ({
            id: uuidv4(),
            file,
            url: URL.createObjectURL(file),
            status: "uploading",
            presignedURL: "",
            publicURL: "",
            progress: 0
          }) satisfies FileUpload
      );

      setFileUploads(current => [...current, ...newFileUploads]);

      const pendingIds = newFileUploads.map(f => f.id);

      let presignedURLs: IFileAttachment[] = [];

      try {
        presignedURLs = await getChatPresignedUrls({
          files: newFileUploads.map(f => ({
            id: f.id,
            name: f.file.name,
            type: f.file.type
          }))
        });

        setFileUploads(current => {
          return current.map(f => {
            const relatedPresignedURL = presignedURLs.find(p => p.id === f.id);

            if (relatedPresignedURL) {
              return {
                ...f,
                presignedURL: relatedPresignedURL.presigned_url,
                publicURL: relatedPresignedURL.public_url
              };
            }

            return f;
          });
        });
      } catch (error) {
        return setFileUploads(current =>
          current.map(f =>
            pendingIds.includes(f.id) ? { ...f, status: "error" } : f
          )
        );
      }

      newFileUploads.forEach(fileUpload => {
        const relatedPresignedURL = presignedURLs.find(
          p => p.id === fileUpload.id
        );

        if (!relatedPresignedURL) {
          return setFileUploads(current =>
            current.map(f =>
              f.id === fileUpload.id ? { ...f, status: "error" } : f
            )
          );
        }

        uploadFile({
          file: fileUpload.file,
          uploadURL: relatedPresignedURL.presigned_url,
          withContentType: true,
          onUploadProgress: (progress: number) => {
            setFileUploads(current =>
              current.map(f =>
                f.id === fileUpload.id
                  ? {
                      ...f,
                      progress
                    }
                  : f
              )
            );
          }
        })
          .then(() => {
            setFileUploads(current =>
              current.map(f =>
                f.id === fileUpload.id ? { ...f, status: "completed" } : f
              )
            );
          })
          .catch(() => {
            setFileUploads(current =>
              current.map(f =>
                f.id === fileUpload.id ? { ...f, status: "error" } : f
              )
            );
          });
      });
    }, []);

    const handleReject = useCallback(() => {
      notifications.show({
        title: "File upload failed",
        message: "Please check the file size and type. (Max size: 20MB)",
        color: "red"
      });
    }, []);

    const isUploading = useMemo(() => {
      return fileUploads.some(f => f.status === "uploading");
    }, [fileUploads]);

    const canSend = chatStatus === "ready" || chatStatus === "error";

    const handleSend = useCallback(() => {
      if (!message.trim() || isUploading || !canSend) return;

      const validFileUploads = fileUploads.filter(
        f => f.status === "completed"
      );

      onSend(message, validFileUploads);
      setMessage("");

      fileUploads.forEach(fileUpload => {
        URL.revokeObjectURL(fileUpload.url);
      });

      setFileUploads([]);
    }, [message, isUploading, fileUploads, onSend, canSend, setMessage]);

    return (
      <Dropzone
        openRef={openRef}
        onDrop={handleDrop}
        onReject={handleReject}
        activateOnClick={false}
        activateOnKeyboard={false}
        maxSize={20 * 1024 * 1024} // 20MB
        accept={acceptedUploadFileExtensions}
        className={classes.wrapper}
      >
        {fileUploads.length ? (
          <Group gap="xs" px="md" mb="xs">
            {fileUploads.map(fileUpload => (
              <ChatInputFile
                key={fileUpload.id}
                name={fileUpload.file.name}
                fileType={fileUpload.file.type}
                url={fileUpload.url}
                progress={fileUpload.progress}
                status={fileUpload.status}
                onRemove={() =>
                  setFileUploads(current =>
                    current.filter(f => f.id !== fileUpload.id)
                  )
                }
              />
            ))}
          </Group>
        ) : null}

        <ChatTextInput
          value={message}
          onChange={setMessage}
          onSend={handleSend}
          ref={ref}
        />

        <Group justify="space-between" p="md">
          <Tooltip label="Add files">
            <ActionIcon
              variant="light"
              size="md"

              color="dark"
              onClick={() => openRef.current?.()}
              style={{ pointerEvents: "all" }}
            >
              <TbPlus />
            </ActionIcon>
          </Tooltip>

          <ActionIcon
            variant="filled"
            color="violet"
            size="md"
            onClick={canSend ? handleSend : onStop}
            disabled={isUploading}
          >
            {canSend ? <TbArrowUp /> : <TbSquare />}
          </ActionIcon>
        </Group>
      </Dropzone>
    );
  }
);

export default memo(ChatInput);
